import { useEffect, useState } from 'react';
import { HyruleToken } from '@nintendo/hyrule-react-commons';
import { gql } from '@apollo/client';
import { GamePlayDataModel } from './GamePlayDataModel';
import Utils from '../../../shared/Utils';
import { GameSummaryApiModel } from '../../../api/models';
import { GamePlaySummaryApi } from '../../../api';

export const NCOM_GRAPH_BATCH_SIZE = 15;

interface GamePlayHookData {
    data: GamePlayDataModel[];
    loading: boolean;
    error: boolean;
    optedOut: boolean;
}

export interface Product {
    name?: string;
    productImage?: {
        publicId?: string;
        url?: string;
    };
}

export const GET_NCOM_AUTH_TOKEN = gql`
    mutation GenerateTokens {
        generateGuestTokens {
            customerToken
        }
    }
`;

export type PRODUCT_BY_APPID_QUERY = {
    request: {
        query: typeof NCOM_PRODUCT_BY_ID;
        variables: {
            input: {
                applicationIds: string[];
            };
        };
    };
    result: {
        data: {
            products: [
                {
                    name: string;
                    sku: string;
                    applicationId: string;
                    productImage: {
                        url: string;
                    };
                },
            ];
        };
    };
};

export const NCOM_PRODUCT_BY_ID = gql`
    query Products($input: ProductsInput) {
        products(input: $input) {
            name
            applicationId
            productImage {
                url
                type
                resourceType
                publicId
            }
        }
    }
`;

export const SEARCH_PRODUCTS = gql`
    query productSearchByName($searchText: String) {
        productSearch(input: { query: $searchText }) {
            nodes {
                applicationId
            }
        }
    }
`;

export const useGamePlay = (
    naid: string,
    token: HyruleToken,
    correlationId: string,
): GamePlayHookData => {
    const [data, setData] = useState<GamePlayDataModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [optedOut, setOptedOut] = useState<boolean>(false);

    const getGamePlayData = async (): Promise<void> => {
        setLoading(true);
        try {
            const gameHistorySummaries: GameSummaryApiModel =
                await GamePlaySummaryApi.getGameHistorySummaries(naid, token, correlationId);

            if (gameHistorySummaries.optedOut) {
                setOptedOut(gameHistorySummaries.optedOut);
            } else {
                const { playedTitles } = gameHistorySummaries;
                const batches = [];

                for (let i = 0; i < playedTitles.length; i += NCOM_GRAPH_BATCH_SIZE) {
                    const batch = playedTitles
                        .slice(i, i + NCOM_GRAPH_BATCH_SIZE)
                        .map((game) => game.titleId);
                    batches.push(batch);
                }
                setData(GamePlaySummaryApi.mapToGamePlayDataModelList(gameHistorySummaries, naid));
            }

            setError(false);
            setLoading(false);
        } catch (error) {
            console.log(`Error fetching game play data for NAID: ${naid}`, error);
            setData([]);
            setError(true);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Utils.isValidNAID(naid) && token.accessToken) {
            getGamePlayData();
        } else {
            setData([]);
        }
    }, [naid, token]);

    return {
        data,
        loading,
        error,
        optedOut,
    };
};
